import React, {FC, useState} from "react"
import {useQueryCustomer} from "@/service/customer.s";
import {Rating, Tab, Stack} from "@mui/material";
import {checkQueryKey, formatMoney, formatTime, getRealId} from "@/helper/method";
import {Grid, Box} from "@mui/material";
import {GridItem} from "@/widget/GridItem/GridItem";
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Chip from "@mui/material/Chip";
import {ResultWrapper} from "@/widget/ResultWrapper/ResultWrapper";
import isEmpty from "lodash/isEmpty";
import {Face} from "@mui/icons-material";
import {GridColDef} from "@mui/x-data-grid";
import {ProTable} from "@/widget/ProTable/ProTable";
import {FieldworkPageVO, useQueryFieldworkPage} from "@/service/fieldwork.s";
import {PaybackTable} from "@/widget/CommonDetail/PaybackTable";
import {InvoiceTable} from "@/widget/CommonDetail/InvoiceTable";
import {ChanceTable} from "@/widget/CommonDetail/ChanceTable";
import {ModeType, useDialogModal, useDrawerModal} from "@/store/global.c";
import {ContactPageVO, useQueryContactPage} from "@/service/contact.s";
import {CheckOutlined, ClearOutlined} from '@mui/icons-material';
import {ContractTable} from "@/widget/CommonDetail/ContractTable";
import {SalesRecord} from "@/pages/Customer/SalesRecord";
import Typography from "@mui/material/Typography";

interface ViewCustomerProps {
    mode: ModeType
}

export const ViewCustomer: FC<ViewCustomerProps> = ({mode}) => {
    const {id: drawerId} = useDrawerModal()
    const {id: dialogId} = useDialogModal()
    const id: number = getRealId(mode, drawerId, dialogId)
    const {data: customerData, isLoading: customerLoading} = useQueryCustomer({id}, checkQueryKey(id))
    const [tab, setTab] = useState<number>(8)
    const {mutateAsync: contactPageMutate, data: contactPageData, isPending: contactPending} = useQueryContactPage()
    const {
        mutate: fieldworkPageMutate,
        data: fieldworkPageData,
        isPending: fieldworkPagePending,
    } = useQueryFieldworkPage()
    const customer = customerData?.data

    const contactColumns: GridColDef<ContactPageVO>[] = [
        {
            field: "name",
            headerName: "姓名",
            headerAlign: "left",
            align: "left",
        },
        {
            field: "mobile",
            headerName: "联系电话",
            headerAlign: "center",
            align: "center",
            flex: 0.6
        },
        {
            field: "firsted",
            headerName: "首要联系人",
            headerAlign: "center",
            align: "center",
            display: 'flex',
            flex: 0.5,
            renderCell: ({row: {firsted}}) => {
                return firsted == 1 ? <CheckOutlined color={'disabled'}/> : <ClearOutlined color={'disabled'}/>
            }
        },
        {
            field: "gender",
            headerName: "性别",
            headerAlign: "center",
            align: "center",
            valueFormatter: (value) => value == 1 && "男" || value == 2 && "女" || "保密",
        },
        {
            field: "post",
            headerName: "职位",
            headerAlign: "center",
            align: "center",
        }]

    const fieldworkColumns: GridColDef<FieldworkPageVO>[] = [
        {
            field: "address",
            headerName: "拜访地址",
            headerAlign: "left",
            align: "left",
            flex: 0.8
        },
        {
            field: "contact",
            headerName: "联系人",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            flex: 0.8
        },
        {
            field: "mobile",
            headerName: "联系电话",
            headerAlign: "center",
            align: "center",
            flex: 0.6
        },
        {
            field: "followers",
            headerName: "随行人员",
            headerAlign: "center",
            align: "center",
        }, {
            field: "travelTime",
            headerName: "出行时间",
            headerAlign: "center",
            align: "center",
            valueFormatter: (value) => formatTime("D", value),
        },]

    return <>
        <ResultWrapper isLoading={customerLoading} isEmpty={isEmpty(customer)}>
            <Stack width={'100%'} direction={'column'} gap={2}>
                <Grid
                    spacing={3}
                    container>
                    <GridItem title={'客户名称'} content={customer?.customerName}/>
                    <GridItem title={'负责人'} content={customer?.creator}/>
                    <GridItem title={'协作人员'}
                              content={<Chip sx={{mr: 0.4}} size={'small'} icon={<Face/>} label={customer?.followNames}
                                             variant="outlined"/>}/>
                    <GridItem title={'客户等级'} content={
                        <Rating defaultValue={customer?.level} size={'small'} readOnly={true}/>
                    }/>

                </Grid>
                <Grid
                    container
                    sx={{
                        '--Grid-borderWidth': '1px',
                        borderTop: 'var(--Grid-borderWidth) solid',
                        borderLeft: 'var(--Grid-borderWidth) solid',
                        borderColor: '#f2f2f2',
                        '& > div': {
                            borderRight: 'var(--Grid-borderWidth) solid',
                            borderBottom: 'var(--Grid-borderWidth) solid',
                            borderColor: '#f2f2f2',
                        },
                    }}
                >
                    <Grid
                        minHeight={60}
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <Stack direction={'column'} height={'100%'} alignContent={'center'} alignItems={'center'} justifyContent={'center'}>
                            <Typography color={'textSecondary'}>商机金额</Typography>
                            <Typography color={'textPrimary'} fontWeight={'unset'}>{formatMoney(customer?.chanceMoney) || 0}</Typography>
                        </Stack>
                    </Grid>
                    <Grid
                        minHeight={60}
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <Stack direction={'column'} height={'100%'} alignContent={'center'} alignItems={'center'} justifyContent={'center'}>
                            <Typography color={'textSecondary'}>未收金额</Typography>
                            <Typography color={'textPrimary'} fontWeight={'unset'}>{formatMoney(customer?.unPaybackMoney) || 0}</Typography>
                        </Stack>
                    </Grid>
                    <Grid
                        minHeight={60}
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <Stack direction={'column'} height={'100%'} alignContent={'center'} alignItems={'center'} justifyContent={'center'}>
                            <Typography color={'textSecondary'}>已收金额</Typography>
                            <Typography color={'textPrimary'} fontWeight={'unset'}>{formatMoney(customer?.paybackMoney) || 0}</Typography>
                        </Stack>
                    </Grid>
                    <Grid
                        minHeight={60}
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <Stack direction={'column'} height={'100%'} alignContent={'center'} alignItems={'center'} justifyContent={'center'}>
                            <Typography color={'textSecondary'}>支出金额</Typography>
                            <Typography color={'textPrimary'} fontWeight={'unset'}>{formatMoney(customer?.expenseMoney) || 0}</Typography>
                        </Stack>
                    </Grid>

                    <Grid
                        minHeight={60}
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <Stack direction={'column'} height={'100%'} alignContent={'center'} alignItems={'center'} justifyContent={'center'}>
                            <Typography color={'textSecondary'}>项目数量</Typography>
                            <Typography color={'textPrimary'} fontWeight={'unset'}>{customer?.projectAmount || 0}</Typography>
                        </Stack>
                    </Grid>

                    <Grid
                        minHeight={60}
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <Stack direction={'column'} height={'100%'} alignContent={'center'} alignItems={'center'} justifyContent={'center'}>
                            <Typography color={'textSecondary'}>专利数量</Typography>
                            <Typography color={'textPrimary'} fontWeight={'unset'}>{customer?.patentAccount || 0}</Typography>
                        </Stack>
                    </Grid>

                    <Grid
                        minHeight={60}
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <Stack direction={'column'} height={'100%'} alignContent={'center'} alignItems={'center'} justifyContent={'center'}>
                            <Typography color={'textSecondary'}>商标数量</Typography>
                            <Typography color={'textPrimary'} fontWeight={'unset'}>{customer?.trademarkAmount || 0}</Typography>
                        </Stack>
                    </Grid>
                    <Grid
                        minHeight={60}
                        size={{
                            xs: 3,
                            sm: 3,
                            md: 3,
                            lg: 3,
                        }}
                    >
                        <Stack direction={'column'} height={'100%'} alignContent={'center'} alignItems={'center'} justifyContent={'center'}>
                            <Typography color={'textSecondary'}>版权数量</Typography>
                            <Typography color={'textPrimary'} fontWeight={'unset'}>{customer?.copyrightAmount || 0}</Typography>
                        </Stack>
                    </Grid>
                </Grid>
                <Grid spacing={3} container={true}>
                    <GridItem title={'首要联系人'} content={customer?.contact}/>
                    <GridItem title={'首要联系电话'} content={customer?.mobile}/>
                </Grid>
                <TabContext value={tab}>
                    <Stack direction="column" spacing={2}>
                        <Box sx={{borderBottom: 1, borderTop: 1, pt: 1, borderColor: 'divider'}}>
                            <TabList variant={'fullWidth'} onChange={(event, value) => setTab(value)}>
                                <Tab label="详细信息" value={8}/>
                                <Tab label="跟进记录" value={1}/>
                                <Tab label="联系人" value={2}/>
                                <Tab label="商机" value={3}/>
                                <Tab label="合同" value={4}/>
                                <Tab label="回款" value={5}/>
                                <Tab label="发票" value={6}/>
                                <Tab label="外勤" value={7}/>
                            </TabList>
                        </Box>
                        <TabPanel value={1} sx={{mt: 1, p: 0}}>
                            <SalesRecord customerId={drawerId}/>
                        </TabPanel>
                        <TabPanel value={2} sx={{p: 0}}>
                            <ProTable<ContactPageVO> loading={contactPending} autoHeight={false} checkboxSelection={false}
                                                     onSearch={(params) => contactPageMutate({...params, customerId: drawerId})}
                                                     data={contactPageData?.data} pageSize={5} showToolbar={false}
                                                     columnHeaderHeight={40}
                                                     columns={contactColumns}/>
                        </TabPanel>
                        <TabPanel value={3} sx={{p: 0}}>
                            <ChanceTable customerId={drawerId}/>
                        </TabPanel>
                        <TabPanel value={4} sx={{p: 0}}>
                            <ContractTable customerId={customer?.id}/>
                        </TabPanel>
                        <TabPanel value={5} sx={{p: 0}}>
                            <PaybackTable customerId={customer?.id}/>
                        </TabPanel>
                        <TabPanel value={6} sx={{p: 0}}>
                            <InvoiceTable customerId={customer?.id}/>
                        </TabPanel>
                        <TabPanel value={7} sx={{p: 0}}>
                            <ProTable<FieldworkPageVO> checkboxSelection={false} columnHeaderHeight={40} loading={fieldworkPagePending}
                                                       onSearch={(params) => fieldworkPageMutate({...params, customerId: drawerId})}
                                                       data={fieldworkPageData?.data} pageSize={5} showToolbar={false} autoHeight={false}
                                                       columns={fieldworkColumns}/>
                        </TabPanel>

                        <TabPanel value={8} sx={{p: 0}}>
                            <Grid container={true} spacing={3}>
                                <GridItem title={'所属行业'} content={customer?.industry}/>
                                <GridItem title={'所在地区'} content={customer?.location}/>
                                <GridItem minHeight={'48px'} size={12} title={'备注'} content={customer?.remark || '-'}/>
                                <GridItem title={'最后跟进时间'} content={formatTime("D", customer?.updatedAt)}/>
                                <GridItem title={'最后跟进人'} content={'-'}/>
                                <GridItem title={'转手次数'} content={0}/>
                                <GridItem title={'创建时间'} content={formatTime("D", customer?.createdAt)}/>
                            </Grid>
                        </TabPanel>
                    </Stack>
                </TabContext>
            </Stack>
        </ResultWrapper>
    </>
};


